import { useLazyVariantsQuery } from "@/shared/api";
import { Cross2Icon } from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Checkbox,
  ChevronDownIcon,
  Flex,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import { MultiSelect, Option } from "react-multi-select-component";
import { twMerge } from "tailwind-merge";

type OptionWithImage = Option & { image?: string };

type VariantMultiSelectProps = {
  value: Option[];
  onValueChange: (value: Option[]) => void;
};

export function VariantMultiSelect({
  value,
  onValueChange,
}: VariantMultiSelectProps) {
  const [fetchVariants, { data, isFetching }] = useLazyVariantsQuery();

  const options = useMemo(() => {
    if (!data) return [];

    return data.results.map((item) => ({
      label: item.display_name,
      value: item.id,
      image: item.image,
    }));
  }, [data]);

  const filterOptions = useCallback(
    async (options: Option[], filter: string) => {
      if (!filter) {
        return value;
      }

      const result = await fetchVariants({ search: filter });

      if (!result.data) {
        return options;
      }

      return result.data.results.map((item) => ({
        label: item.display_name,
        value: item.id,
        image: item.image,
      }));
    },
    [value, fetchVariants]
  );

  return (
    <MultiSelect
      className="variant-multi-select"
      isLoading={isFetching}
      options={options}
      value={value}
      onChange={onValueChange}
      labelledBy="SKU selection"
      filterOptions={filterOptions}
      debounceDuration={400}
      ItemRenderer={DefaultItemRenderer}
      ClearSelectedIcon={<Cross2Icon />}
      ClearIcon={<Cross2Icon />}
      ArrowRenderer={ArrowRenderer}
      valueRenderer={ValueRenderer}
      hasSelectAll={false}
    />
  );
}

type DefaultItemRendererProps = {
  checked: boolean;
  option: OptionWithImage;
  disabled?: boolean;
  onClick?: () => void;
};

function DefaultItemRenderer({
  checked,
  option,
  disabled,
  onClick,
}: DefaultItemRendererProps) {
  return (
    <div
      className={`item-renderer select-none flex items-center gap-2 pl-1 ${disabled ? "disabled" : ""}`}
    >
      <Checkbox
        onCheckedChange={onClick}
        checked={checked}
        tabIndex={-1}
        disabled={disabled}
      />

      <figure className="min-w-12 w-12 h-12">
        <img
          className="object-contain w-full h-full"
          src={option.image || "/placeholder.jpg"}
        />
      </figure>

      <Text size="1">{option.label}</Text>
    </div>
  );
}

function ValueRenderer(selected: Option[]) {
  if (selected.length === 0) {
    return (
      <Text size="2" ml="1" className="text-[var(--gray-a10)]">
        Select SKU
      </Text>
    );
  }

  return (
    <Flex gap="1">
      {selected.slice(0, 2).map((item) => (
        <Tooltip content={item.label} key={item.value}>
          <Badge
            color="gray"
            className={twMerge([
              selected.length === 1 && "max-w-[190px]",
              selected.length === 2 && "max-w-[94px]",
              selected.length > 2 && "max-w-[80px]",
            ])}
          >
            <Text className="truncate">{item.label}</Text>
          </Badge>
        </Tooltip>
      ))}
      {selected.length > 2 && (
        <Badge color="gray">+{selected.length - 2}</Badge>
      )}
    </Flex>
  );
}

function ArrowRenderer() {
  return (
    <Box pl="1">
      <ChevronDownIcon width={10} height={10} />
    </Box>
  );
}
